import {
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '@store/index';
import { baseUrl } from '../utils/url';
import { ISystemDialog } from '@store/slices/user.slice';
import {
  setExpiredSession,
  setIsMaintenance,
  setSystemDialogs,
} from '@store/slices/api.slice';
import { getCurrentModule } from '@hooks/useCurrentModule';

const tokenError = [
  'invalid token',
  'invalidtoken',
  'invalid authentication',
  'invalidauthentication',
];

type ErrorResponse = {
  data: {
    error_message: string;
    errors: {
      error: {
        remarks: string | { name: string; message: string; expiredAt: string };
      };
    }[];
    isMaintenanceMode?: number;
    systemDialogs?: ISystemDialog[];
  };
  message?: string;
  status?: number;
  errorCode: string;
};

/**
 * Configures and exports a base query function for API requests.
 * This query function is set up with common configurations and header preparations.
 */

export const baseQuery: BaseQueryFn<
  string | { url: string; method?: string; body?: unknown },
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await fetchBaseQuery({
    baseUrl: baseUrl,
    mode: 'cors',
    credentials: 'include',
    prepareHeaders: async (headers) => {
      const data = api.getState() as RootState;

      if (data) {
        if (
          !headers.get('propertyID') &&
          data.properties.selectedProperty.value !== 'All Properties'
        ) {
          headers.set('propertyID', data.properties.selectedProperty.value);
        }

        const customStationID = headers.get('customStationID');

        if (customStationID) {
          headers.set('stationID', customStationID);
        } else if (data?.posStations?.selectedStation?.stationID) {
          headers.set('stationID', data.posStations.selectedStation.stationID);
        }

        const currentModule = getCurrentModule();
        const isPOSModule = currentModule === 'POS' || currentModule === 'BO';

        if (isPOSModule && data?.posStations?.selectedStation?.subModuleCode) {
          headers.set(
            'subModule',
            data?.posStations?.selectedStation?.subModuleCode,
          );
        } else {
          headers.set('subModule', '');
        }

        if (
          data.selectedAccountingPeriod.selectedAccountingPeriod
            .accountingPeriodID
        ) {
          headers.set(
            'accountingPeriod',
            data.selectedAccountingPeriod.selectedAccountingPeriod.periodName,
          );
        }
        headers.set('x-access-token', data.user.info.token);
      }
      return headers;
    },
  })(args, api, extraOptions);

  // TOAST
  // enqueueSnackbar('success');

  // MODAL OR DIALOG

  if (result.error?.data) {
    const errorData = result.error.data as ErrorResponse;

    // 1. Maintenance mode
    if (Boolean(errorData?.data?.isMaintenanceMode)) {
      api.dispatch(setIsMaintenance(true));
      api.dispatch(setSystemDialogs(errorData?.data?.systemDialogs!));
      return result;
    }

    // 2. Session expired — old shape
    if (
      errorData?.data?.error_message &&
      tokenError.includes(errorData.data.error_message.toLowerCase()) &&
      errorData.data.errors?.some(({ error }) => {
        if (typeof error.remarks === 'string') {
          return error.remarks.toLowerCase().includes('expired');
        }
        return error.remarks.name.toLowerCase().includes('expired');
      })
    ) {
      api.dispatch(setExpiredSession(true));
      return result;
    }

    // 3. Session expired — new shape
    if (
      (result.error?.status === 401 || errorData?.status === 401) &&
      ['expired token', 'expired'].some((keyword) =>
        errorData?.message?.toLowerCase().includes(keyword),
      )
    ) {
      api.dispatch(setExpiredSession(true));
      return result;
    }
  }

  return result;
};
